import { Stack, useMediaQuery, useTheme, Grid, Box } from '@mui/material';
import { PermitCooldown, UnitOfTime } from 'types';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import TimeUnitSelect from 'components/inputs/TimeUnitSelect';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaDropdown from 'components/inputs/AuturaDropdown';

interface PermitCooldownWidgetProps {
  cooldown: PermitCooldown | undefined;
  setCooldown: (cooldown: PermitCooldown) => void;
  readonly?: boolean;
  disabled?: boolean;
}

export const PermitCooldownWidget = ({
  cooldown = {
    enabled: false,
    target: 'vehicle',
    rate: 1,
    unitOfTime: UnitOfTime.Year,
    amountOfTime: 1,
  },
  setCooldown,
  readonly = false,
  disabled = false,
}: PermitCooldownWidgetProps): ReactNode => {
  const { t } = useTranslation();

  const [rateLimitEnabled, setRateLimitEnabled] = useState<boolean>(
    cooldown.enabled,
  );
  const [unitOfTime, setUnitOfTime] = useState<UnitOfTime>(cooldown.unitOfTime);
  const [target, setTarget] = useState<'vehicle' | 'person'>(cooldown.target);
  const [rate, setRate] = useState<number>(cooldown.rate);

  useEffect(() => {
    if (disabled) {
      setRateLimitEnabled(false);
    }
  }, [disabled]);

  const handleAmountOfTimeChange = (amountOfTime: number | string) => {
    const numeric = amountOfTime
      .toString()
      .replace(/[^0-9]/g, '')
      .trim();
    setCooldown({
      ...cooldown,
      amountOfTime: Number(numeric) || 0,
    });
  };

  const handleUnitOfTimeChange = (value: UnitOfTime) => {
    setUnitOfTime(value);
    setCooldown({
      ...cooldown,
      unitOfTime: value,
    });
  };

  const handleRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const digits = value.replace(/[^0-9]/g, '');

    setRate(parseInt(digits));
    setCooldown({
      ...cooldown,
      rate: parseInt(digits),
    });
  };

  const handleRateLimitEnabledChange = (value: boolean) => {
    setRateLimitEnabled(value);
    setCooldown({
      ...cooldown,
      enabled: value,
    });
  };

  const handleTargetChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTarget(event.target.value as 'vehicle' | 'person');
    setCooldown({
      ...cooldown,
      target: event.target.value as 'vehicle' | 'person',
    });
  };

  const calcDisabled = disabled || !rateLimitEnabled;

  return (
    <Stack display='flex' flexDirection={'column'} gap={2}>
      <Box flex={1}>
        <AuturaSwitch
          name='rateLimitToggle'
          label={`${t('permit_type.data.cooldown.label', {
            ns: 'entities',
          })} ${t('labels.self_service_only', { ns: 'permitTypes' })}`}
          value={rateLimitEnabled}
          setValue={handleRateLimitEnabledChange}
          helperText={t('helpers.rate_limit', { ns: 'permitTypes' })}
          readonly={readonly}
          disabled={disabled}
        />
      </Box>
      <Grid container width={'100%'} spacing={1}>
        <Grid size={1}>
          <AuturaTextField
            notched={false}
            name='rate'
            value={rate}
            label={''}
            type='number'
            disabled={calcDisabled}
            readonly={readonly}
            onChange={handleRateChange}
            required={rateLimitEnabled}
            inputSx={{
              textAlign: 'center',
            }}
          />
        </Grid>
        <Grid size={2}>
          <AuturaTextField
            notched={false}
            name='permitsPer'
            value={'permits per'}
            disabled={calcDisabled}
            fullWidth={false}
            variant={'standard'}
            margin={'dense'}
            disableUnderline
            readonly={true}
            inputSx={{
              textAlign: 'center',
              marginTop: '2px',
            }}
          />
        </Grid>
        <Grid size={2.5}>
          <AuturaDropdown
            label={''}
            notched={false}
            name='target'
            value={target}
            disabled={calcDisabled}
            readonly={readonly}
            setValue={handleTargetChange}
            options={['vehicle', 'person']}
          />
        </Grid>
        <Grid size={1}>
          <AuturaTextField
            notched={false}
            name='every'
            value={'every'}
            disabled={calcDisabled}
            variant={'standard'}
            margin={'dense'}
            disableUnderline
            readonly={true}
            label={''}
            required={rateLimitEnabled}
            inputSx={{
              textAlign: 'center',
              marginTop: '2px',
            }}
          />
        </Grid>
        <Grid size={5}>
          <TimeUnitSelect
            label={''}
            notched={false}
            unitOfTime={unitOfTime}
            disabled={calcDisabled}
            readonly={readonly}
            setUnitOfTime={handleUnitOfTimeChange}
            allowForever={false}
            amountOfTime={cooldown.amountOfTime}
            setAmountOfTime={handleAmountOfTimeChange}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
